
import { yupResolver } from '@hookform/resolvers/yup'
import { Box } from '@mui/material'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { acPostAutoLogin, acPostLogin } from '../../store/actions'
import { PrimaryButton, Alert } from '../default'

import { FormInputText, FormInputPassword } from './formInputs'
import { FormValidationLogin } from './formValidations'

const defaultValues = {
    email: '',
    password: ''
}

/**
 * Renders a login form component.
 * Displays a login form with email and password fields. It uses the react-hook-form library for form validation and submission.
 *
 * @author Maria Eduarda Franke
 * @returns {JSX.Element} The rendered login form component.
 */
export const LoginForm = () => {

    const dispatch = useDispatch()
    const location = useLocation()

    const successAlert = location.state ? location.state.successAlert : null

    const isLoadingLogin = useSelector(state => state.auth.isLoadingLogin)
    const hasErrorLogin = useSelector(state => state.auth.hasErrorLogin)

    useEffect(() => {
        const url = new URL(window.location.href)
        const token = url.searchParams.get('token')

        if (token)
            dispatch(acPostAutoLogin({ token }))
                .catch(error => console.error(error))
        // eslint-disable-next-line
    }, [])

    const { control, handleSubmit, formState: { errors } } = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(FormValidationLogin)
    })

    const onSubmit = handleSubmit((data) => {
        dispatch(acPostLogin(data))
            .catch(error => console.error(error))
    })

    const getErrorMessage = () => {

        if (!hasErrorLogin) return ''

        const remainingAttempts = parseInt(hasErrorLogin.headers['ratelimit-remaining'])

        switch (hasErrorLogin.description) {
            case 'TOKEN_INVALID':
                return 'Token inválido! Realize o login com suas credenciais de acesso.'
            case 'TOKEN_EXPIRED':
                return 'Token expirado! Realize o login com suas credenciais de acesso.'
            case 'USER_NOT_FOUND':
                return remainingAttempts > 2 ? 'Informações inválidas!' : `Informações inválidas! Restam ${remainingAttempts + 1} tentativas.`
            case 'TOO_MANY_ATTEMPTS':
                return 'Número de tentativas excedido. Tente novamente mais tarde!'
            default:
                return 'Erro ao efetuar login!'
        }
    }

    return (
        <Box component='form' onSubmit={onSubmit} noValidate>
            {
                hasErrorLogin && (
                    <Alert severity='error' message={getErrorMessage()} sx={{ mb: 1 }} />
                )
            }
            {
                (successAlert && !hasErrorLogin) && (
                    <Alert
                        severity='success'
                        message={successAlert}
                        sx={{ mb: 1 }}
                    />
                )
            }
            <FormInputText
                name='email'
                label='E-mail'
                placeholder='Digite seu e-mail'
                control={control}
                errors={errors.email}
                fullWidth
                margin='normal'
                required

            />
            <FormInputPassword
                name='password'
                label='Senha'
                placeholder='Digite sua senha'
                control={control}
                errors={errors.password}
                fullWidth
                margin='normal'
                required
            />
            <PrimaryButton
                label='Acessar'
                loading={isLoadingLogin}
                type='submit'
                fullWidth
                sx={{ mt: 2 }}
            />
        </Box>
    )

}
